import { useEffect, useState } from "react";
import { Breadcrumb, Button, Cascader, Checkbox, DatePicker, Empty, Form, Input, message, Radio, Select, Space, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useQuery } from "../../common/utils";
import { fetchRegionCascadeList, fetchSaleActivityUserFieldConf, fetchSaleActivityUserInfo, submitSaleActivityUser } from "./api";
import { SaleActivityUserFieldConf } from "./types";
import UnitField from "./UnitField";
import "./activity.scss";

const splitOptions = (text?: string) =>
  (text || "")
    .split(/[|｜,，;；\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

const isRequired = (field: SaleActivityUserFieldConf) =>
  field.required === true || field.required === 1;

const isUploadField = (field: SaleActivityUserFieldConf) => {
  const type = String(field.fieldType || "").toLowerCase();
  return type.includes("upload") || type.includes("file");
};

const mobilePattern = /^1[3-9]\d{9}$/;

const getFieldRules = (field: SaleActivityUserFieldConf) => {
  const rules: any[] = [{ required: isRequired(field), message: `请填写${field.zhName}` }];
  if (field.enName === "mobile") {
    rules.push({ pattern: mobilePattern, message: "请输入正确的手机号" });
  }
  return rules;
};

const getResponseData = (data: any) =>
  data?.saleActivityUser || data?.activityUserInfo || data?.userInfo || data || {};

const normalizeRegionTree = (list?: any[]): any[] =>
  (list || []).map((item) => ({
    ...item,
    value: item.value || item.code || item.regionCode || item.label || item.name,
    label: item.label || item.name || item.regionName || item.value,
    children: item.children && item.children.length ? normalizeRegionTree(item.children) : undefined,
  }));

const getRegionValuesByLabels = (options: any[], labels: string[] = []) => {
  const values: string[] = [];
  let currentOptions = options;
  labels.forEach((label) => {
    const item = currentOptions.find((option) => option.label === label || option.value === label);
    if (item) {
      values.push(item.value);
      currentOptions = item.children || [];
    }
  });
  return values;
};

const getCascadeValueFromField = (field: SaleActivityUserFieldConf, options: any[]) => {
  if (!field.values) return [];
  const list = String(field.values).split("/").map((item) => item.trim()).filter(Boolean);
  if (list.length === 0) return [];
  const region = getRegionValuesByLabels(options, list);
  return region.length > 0 ? region : list;
};

const isRegionField = (field: SaleActivityUserFieldConf) =>
  field.enName === "region" || field.zhName === "单位所在地" || field.zhName === "所在省市区";

const isBaseField = (field: SaleActivityUserFieldConf) =>
  field.enName === "base" || field.zhName === "所属基地";

const normalizeUserFormValues = (data: any, fieldList: SaleActivityUserFieldConf[], regionOptions: any[] = []) => {
  const source = getResponseData(data);
  const values: any = { ...source };
  fieldList.forEach((field) => {
    const value = source[field.enName];
    const type = String(field.fieldType || "").toLowerCase();
    const fieldValue = value === undefined || value === null || value === "" ? field.values : value;
    if (fieldValue === undefined || fieldValue === null || fieldValue === "") return;
    if (isRegionField(field)) {
      values[field.enName] = Array.isArray(fieldValue)
        ? fieldValue
        : getCascadeValueFromField({ ...field, values: String(fieldValue) }, regionOptions);
      return;
    }
    if (isBaseField(field)) {
      values[field.enName] = Array.isArray(fieldValue)
        ? fieldValue
        : String(fieldValue).split("/").map((item) => item.trim()).filter(Boolean);
      return;
    }
    if (type.includes("checkbox")) {
      values[field.enName] = Array.isArray(fieldValue) ? fieldValue : splitOptions(String(fieldValue));
      return;
    }
    if (type.includes("date")) {
      const dateValue = moment(fieldValue);
      values[field.enName] = dateValue.isValid() ? dateValue : fieldValue;
      return;
    }
    values[field.enName] = fieldValue;
  });
  if (Array.isArray(source.region) && source.region.length > 0) {
    values.region = source.region;
  } else if (source.province || source.city || source.district) {
    const region = getRegionValuesByLabels(
      regionOptions,
      [source.province, source.city, source.district].filter(Boolean)
    );
    values.region = region.length > 0
      ? region
      : [source.province, source.city, source.district].filter(Boolean);
  }
  fieldList.filter(isRegionField).forEach((field) => {
    values[field.enName] = values.region;
  });
  if (Array.isArray(source.base) && source.base.length > 0) {
    values.base = source.base;
  } else if (source.instituteType || source.instituteProvince || source.instituteName) {
    values.base = [source.instituteType, source.instituteProvince, source.instituteName].filter(Boolean);
  }
  return values;
};

const getUploadFileList = (value?: string) => {
  if (!value) return [];
  const fileName = value.split("/").filter(Boolean).pop() || "已上传附件";
  return [{
    uid: value,
    name: fileName,
    status: "done" as const,
    url: value,
  }];
};

export default function SaleActivityUserSignUp() {
  const query = useQuery();
  const history = useHistory();
  const saleActivityId = query.get("activityId") || "";
  const [form] = Form.useForm();
  const [fields, setFields] = useState<SaleActivityUserFieldConf[]>([]);
  const [uploadFileMap, setUploadFileMap] = useState<any>({});
  const [regionOptions, setRegionOptions] = useState<any[]>([]);

  useEffect(() => {
    if (!saleActivityId) return;
    Promise.all([fetchRegionCascadeList(), fetchSaleActivityUserFieldConf(saleActivityId)]).then(([regionRes, res]) => {
      const options = normalizeRegionTree(regionRes.data || []);
      setRegionOptions(options);
      const data = res.data || {};
      const fieldList = data.nowFieldList || data.fieldList || [];
      setFields(fieldList);
      if (data.userMobile) {
        form.setFieldsValue({ mobile: data.userMobile });
      }
      fetchSaleActivityUserInfo(saleActivityId).then((infoRes) => {
        const info = getResponseData(infoRes.data);
        const values = normalizeUserFormValues(info, fieldList, options);
        if (data.userMobile && !values.mobile) {
          values.mobile = data.userMobile;
        }
        form.setFieldsValue(values);
        const nextUploadFileMap: any = {};
        fieldList.filter(isUploadField).forEach((field: SaleActivityUserFieldConf) => {
          nextUploadFileMap[field.enName] = getUploadFileList(values[field.enName]);
        });
        setUploadFileMap(nextUploadFileMap);
      });
    });
  }, [saleActivityId, form]);

  const uploadRequest = (fieldName: string) => async (data: any) => {
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("uploadDataType", "file");
    formData.append("folder", "saleActivity");
    try {
      const response: any = await axios.post("/shop/api/upload/file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.code === 0 || response.data.code === 200) {
        const file = response.data.data || {};
        const value = file.shortPathFile || file.fullPathFile || file.fileUrl;
        form.setFieldsValue({ [fieldName]: value });
        setUploadFileMap((prev: any) => ({
          ...prev,
          [fieldName]: [{
            uid: value || data.file.uid,
            name: data.file.name,
            status: "done",
            url: value,
          }],
        }));
        data.onSuccess(response.data);
        message.success("上传成功");
      } else {
        data.onError(response.data);
        message.error(response.data.msg || response.data.message || "上传失败");
      }
    } catch (error) {
      data.onError(error);
      message.error("上传失败");
    }
  };

  const renderField = (field: SaleActivityUserFieldConf) => {
    const type = String(field.fieldType || "").toLowerCase();
    const options = splitOptions(field.optionsContent || field.values);
    if (field.enName === "unit") {
      return <UnitField form={form} mode="user" />;
    }
    if (isRegionField(field)) {
      return (
        <Cascader
          allowClear
          showSearch
          options={regionOptions}
          placeholder={field.placeholder || "请选择所在省市区"}
        />
      );
    }
    if (type.includes("select")) {
      return (
        <Select placeholder={field.placeholder}>
          {options.map((item) => <Select.Option key={item} value={item}>{item}</Select.Option>)}
        </Select>
      );
    }
    if (type.includes("radio")) {
      return <Radio.Group>{options.map((item) => <Radio key={item} value={item}>{item}</Radio>)}</Radio.Group>;
    }
    if (type.includes("checkbox")) {
      return <Checkbox.Group options={options} />;
    }
    if (type.includes("date")) {
      return <DatePicker style={{ width: "100%" }} />;
    }
    if (type.includes("upload") || type.includes("file")) {
      return (
        <>
          <Upload
            customRequest={uploadRequest(field.enName)}
            fileList={uploadFileMap[field.enName] || []}
            maxCount={1}
            onRemove={() => {
              form.setFieldsValue({ [field.enName]: undefined });
              setUploadFileMap((prev: any) => ({ ...prev, [field.enName]: [] }));
            }}
          >
            <Button icon={<UploadOutlined />}>上传附件</Button>
          </Upload>
          <Form.Item name={field.enName} noStyle><Input type="hidden" /></Form.Item>
        </>
      );
    }
    if (type.includes("textarea")) {
      return <Input.TextArea rows={4} placeholder={field.placeholder} />;
    }
    return <Input placeholder={field.placeholder} />;
  };

  const submit = () => {
    form.validateFields().then((values) => {
      const params: any = { saleActivityId: Number(saleActivityId) };
      const regionField = fields.find(isRegionField);
      const baseField = fields.find(isBaseField);
      const regionFieldName = regionField?.enName || "region";
      const baseFieldName = baseField?.enName || "base";
      Object.keys(values).forEach((key) => {
        const val = values[key];
        if (key === regionFieldName) {
          params.region = val;
          return;
        }
        if (key === baseFieldName) {
          params.base = val;
          return;
        }
        params[key] = Array.isArray(val)
          ? val.join(",")
          : val && val.format
            ? val.format("YYYY-MM-DD")
            : val;
      });
      if (params.age !== "" && params.age !== undefined && params.age !== null) {
        const age = Number(params.age);
        params.age = Number.isNaN(age) ? params.age : age;
      }
      submitSaleActivityUser(params).then((res) => {
        if (res.code === 0 || res.code === 200 || res.code === 2000) {
          message.success(res.message || res.msg || "提交成功");
          history.push(`/saleActivityDetail?activityId=${saleActivityId}`);
        }
      });
    });
  };

  return (
    <div className="sale-activity-user-signup-page">
      <div className="sale-activity-user-signup-breadcrumb">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>活动报名</Breadcrumb.Item>
          <Breadcrumb.Item>活动详情</Breadcrumb.Item>
          <Breadcrumb.Item>学员报名</Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <div className="sale-activity-user-signup-body">
        <div className="sale-activity-user-signup-title">学员信息填报</div>
        <div className="sale-activity-user-signup-panel">
          <Form
            form={form}
            labelAlign="right"
            labelCol={{ flex: "126px" }}
            wrapperCol={{ flex: "590px" }}
          >
            <div className="sale-activity-user-signup-fields">
              {fields.length > 0 ? fields
                .filter((field) => field.display !== false && field.display !== 0)
                .map((field) =>
                  isUploadField(field) ? (
                    <Form.Item
                      className={`sale-activity-user-signup-item${isRequired(field) ? " sale-activity-user-signup-item--required" : ""}`}
                      key={field.enName}
                      label={field.zhName}
                      required={isRequired(field)}
                    >
                      {renderField(field)}
                    </Form.Item>
                  ) : (
                    <Form.Item
                      className={`sale-activity-user-signup-item${isRequired(field) ? " sale-activity-user-signup-item--required" : ""}`}
                      key={field.enName}
                      name={field.enName}
                      label={field.zhName}
                      rules={getFieldRules(field)}
                    >
                      {renderField(field)}
                    </Form.Item>
                  )
                ) : (
                  <div className="sale-activity-user-signup-empty">
                    <Empty description="暂无可填写字段" />
                  </div>
                )}
            </div>
            <div className="sale-activity-user-signup-actions">
              <Space size={14}>
                <Button onClick={() => history.push(`/saleActivityDetail?activityId=${saleActivityId}`)}>取消</Button>
                <Button type="primary" onClick={submit}>提交报名</Button>
              </Space>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}
